import "styles/questionnaire/taxSummary.less";

import { ChangeEvent, FC, useCallback, useEffect, useState } from "react";

import { CheckSquareOutlined, PhoneOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Form, Input } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import blueInfoIcon from "assets/images/new-questionnaire/blue-info.svg";
import errorIcon from "assets/images/new-questionnaire/exclamationmark-circle.svg";
import questionIcon from "assets/images/new-questionnaire/question_icon.svg";
import { useModalOpener } from "hooks/helpers/useModalOpener";
import { useDeviceData } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { useGetTaxCalculationsMutation, useSetDocumentUtrMutation } from "services/client";
import { IUser } from "types/auth";
import { logGoogleAnalyticsEvent } from "utils/analytics";
import { logError } from "utils/sentry";
import { isUTRValid, normalizeUTR } from "utils/validation";

import TaxSummaryContent from "./TaxSummaryContent";
import { PromptModalType } from "../../enums/enum";
import PromptModal from "../Shared/PromptModal";

type TaxSummaryProps = {
    user: IUser;
    handleSubmit: () => void;
    isMobile: boolean;
}

export const TaxSummary: FC<TaxSummaryProps> = ({
    user, handleSubmit, isMobile }) => {
    const deviceData = useDeviceData(window.navigator.userAgent);
    const { currentDocument } = useAppSelector(state => state.document);
    const navigate = useNavigate();

    const [setDocumentUtr] = useSetDocumentUtrMutation();
    const [getGetTaxCalculations] = useGetTaxCalculationsMutation();
    const [checked, setChecked] = useState<boolean>(false);
    const [taxData, setTaxData] = useState<any | null>(null);
    const [utr, setUtr] = useState<string>(user?.utr || "");
    const [errorUtr, setErrorUtr] = useState<boolean>(false);
    const [utrErrorMessage, setUtrErrorMessage] = useState<string>("");
    const [isVisible, setIsVisible] = useState(false);

    const {
        isOpened: isStepHelpModalOpened,
        open: handleStepHelpOpenModal,
        close: handleStepHelpCloseModal,
    } = useModalOpener();

    const {
        isOpened: isConfirmContinueModalOpened,
        open: handleConfirmContinueOpenModal,
        close: handleConfirmContinueCloseModal,
    } = useModalOpener();

    const {
        isOpened: isUtrModalOpened,
        open: handleUtrOpenModal,
        close: handleUtrCloseModal,
    } = useModalOpener();

    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        setChecked(e.target.checked);
    };

    const goBackToProfile = () => {
        navigate("/profile");
    };

    const handleClick = () => {
        setIsVisible(prev => !prev);
    };

    const handleInputChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value = e.target.value;
            setUtr(value);

            const normalizedUTR = normalizeUTR(value);
            const isValidUtr = isUTRValid(normalizedUTR);
            setErrorUtr(!isValidUtr);
            setUtrErrorMessage(isValidUtr ? "" : "Please enter a valid UTR");

            if (isValidUtr && normalizedUTR) {
                try {
                    await setDocumentUtr({
                        clientId: user.id,
                        documentId: currentDocument?._id,
                        body: {
                            utr: normalizedUTR,
                        },
                    }).unwrap();
                } catch (error: any) {
                    const serverErrorMessage = error?.data?.error || "Failed to set UTR";
                    setErrorUtr(true);
                    setUtrErrorMessage(serverErrorMessage);
                    logError(error, {
                        clientId: user.id,
                        documentId: currentDocument?._id,
                        message: "Failed to set UTR",
                        function: "handleInputChange",
                    });
                }
            }
        },
        [setDocumentUtr, user.id, currentDocument?._id]
    );

    useEffect(() => {
        const fetchTaxCalculations = async () => {
            if (!user?.id || !currentDocument.id) return;
            try {
                const response = await getGetTaxCalculations({
                    clientId: user.id,
                    documentId: currentDocument.id,
                }).unwrap();
                const breakdown = response.breakdown;
                setTaxData(breakdown);
            } catch (error) {
                const errorContext = {
                    clientId: user?.id,
                    message: "Failed to fetch tax calculations",
                    function: "fetchTaxCalculations",
                };
                logError(error, errorContext);
            }
        };

        fetchTaxCalculations();
    }, [getGetTaxCalculations, user, currentDocument]);

    return (
        <div>
            <TaxSummaryContent user={user} taxData={taxData} isVisible={isVisible} handleClick={handleClick}/>
            <Card>
                <div className="tax-summary__utr">Enter Your Unique Tax Reference Number (UTR)</div>
                <p>UTR Number is needed to submit your tax return to HMRC.  You can find your ‘Tax Reference’ number in your HMRC account or letters from HMRC. <span
                    onClick={() => {
                        logGoogleAnalyticsEvent("utr_helper_clicked", {
                            user_id: user?.id,
                            document_id: currentDocument?._id,
                            device: deviceData?.device,
                            os: deviceData?.os,
                            browser: deviceData?.browser,
                            platform: isMobile ? "mobile" : "desktop",
                        });
                        handleUtrOpenModal();
                    }}
                    className="tax-summary__purple_link">Learn More</span>
                </p>
                <Form layout="vertical">
                    <Form.Item label={"Enter Your UTR here"} style={{ maxWidth: 400 }}>
                        <Input
                            size="large"
                            onChange={(e: any) => handleInputChange(e)}
                            value={utr}
                            suffix={
                                <img
                                    src={questionIcon}
                                    alt="icon"
                                    onClick={e => {
                                        logGoogleAnalyticsEvent("utr_helper_clicked", {
                                            user_id: user?.id,
                                            document_id: currentDocument?._id,
                                            device: deviceData?.device,
                                            os: deviceData?.os,
                                            browser: deviceData?.browser,
                                            platform: isMobile ? "mobile" : "desktop",
                                        });
                                        e.stopPropagation();
                                        handleUtrOpenModal();
                                    }}
                                    width={20}
                                    height={20}
                                    className="pointer"
                                />
                            }
                            inputMode="decimal"

                        />
                        {errorUtr &&
                        <div className="new-questionnaire__error-message">
                            <img src={errorIcon} width={20} alt="error" />
                            <div className="new-questionnaire__error-message-text ml4">{utrErrorMessage}</div>
                        </div>
                        }
                    </Form.Item>
                </Form>

                <Checkbox checked={checked} onChange={handleCheckboxChange}>
                    <div className="tax-summary__confirm-box__text">
                        <div>{"I confirm that the information I have provided is accurate, up to date, and complete to the best of my knowledge. I understand that this information will be submitted to HMRC as part of my tax return. "}
                            <span className="tax-summary__purple_link" onClick={handleStepHelpOpenModal}>
                                Learn More
                            </span>
                        </div>
                    </div>
                </Checkbox>
                {!checked && <div className="tax-summary__blue-info"><img src={blueInfoIcon} alt="info box" style={{ cursor: "pointer" }}/>To edit your answers, click the relevant sections (e.g. Personal Info, CIS) in the navigation menu above.</div>}
                <div className="payment-details__bank-details__button-container">
                    <Button
                        onClick={goBackToProfile}
                        type="primary"
                        className="shared__secondary-button"
                        icon={<SaveOutlined />}>
                            Save & Submit Later
                    </Button>
                    <Button
                        type="primary"
                        className="shared__secondary-button"
                        icon={<PhoneOutlined />}
                    >
                        <a href="https://taxzapuk.setmore.com/" target="_blank" rel="noopener noreferrer">
                           Speak with a Tax Advisor
                        </a>
                    </Button>

                    <Button
                        type="primary"
                        htmlType="submit"
                        icon={<CheckSquareOutlined />}
                        className="shared__primary-button"
                        onClick={handleConfirmContinueOpenModal}
                        disabled={!checked || utr === "" || errorUtr}
                    > Confirm & Submit To HMRC
                    </Button>
                </div>
            </Card>
            {isStepHelpModalOpened &&
                <PromptModal
                    isOpen={isStepHelpModalOpened}
                    closeModal={handleStepHelpCloseModal}
                    closeButtonStyle={{ display: "none" }}
                    data={{
                        type: PromptModalType.info,
                        title: "",
                        text: "<div style='font-weight: 600; color: #000; font-size: 18px; text-align: center; margin-bottom: 10px; margin-top: 10px'>The Legal Disclaimer</div>" +
                            "<div style='text-align: left;color: #000;font-size: 16px;'>By checking this box, you acknowledge and agree that:\n\n</div>" +
                            "<div style='text-align: left;color: #000;font-size: 16px;'>    1.  The tax return will be prepared and submitted to HMRC based solely on the information you have provided.\n\n</div>" +
                            "<div style='text-align: left;color: #000;font-size: 16px;'>    2.  You are responsible for ensuring the accuracy and completeness of the information you submit.\n\n</div>" +
                            "<div style='text-align: left;color: #000;font-size: 16px;'>        3.  Any discrepancies or omissions in the provided information could result in penalties or legal action from HMRC, and you accept full responsibility for any such consequences\n\n</div>" +
                            "<div style='text-align: left;color: #000;font-size: 16px;'>    4.  Our role is limited to submitting the information on your behalf, and we are not responsible for verifying its accuracy.\n\n</div>",
                    }}
                    handlePrev={() => {
                    }}
                />
            }

            {isConfirmContinueModalOpened && (
                <PromptModal
                    isOpen={isConfirmContinueModalOpened}
                    closeModal={handleConfirmContinueCloseModal}
                    confirmAction={handleSubmit}
                    handlePrev={() => {}}
                    data={{
                        type: PromptModalType.confirmation,
                        title: "Submitting your tax return to HMRC",
                        text: "<p>Are you sure you want to proceed with this submission? Once you submit your tax return to HMRC, you cannot go back and amend any details. If you are not ready yet, click 'Save & Submit Later'</p>",
                        image: "",
                        confirmButtonText: "Yes, I'm sure",
                        cancelButtonText: "Go Back",
                    }}
                />
            )}
            {isUtrModalOpened && (
                <PromptModal
                    isOpen={isUtrModalOpened}
                    closeModal={handleUtrCloseModal}
                    handlePrev={() => {}}
                    data={{
                        type: PromptModalType.info,
                        title: "UTR",
                        text: `<div style='font-weight: 600; color: #000; font-size: 18px; text-align: left; margin-top: 10px'>What is a UTR number?</div>
                            <div style='text-align: left;'>A Unique Taxpayer Reference number is a 10-digit number issued by HMRC to self-employed individuals or people required to file a Self Assessment tax return.</div>
                            <div style='font-weight: 600; color: #000; font-size: 18px; text-align: left'>How do I find my UTR number?</div>
                            <ul style='text-align: left; padding-left: 5%; margin-top: 0px'
                            <li>In your HMRC Personal Tax Account</li>
                            <li>On documents sent by HMRC</li>
                            </ul>`,
                    }}
                />
            )
            }
        </div>
    );
};
